/**
 * Iter 74t — AI Settings admin panel.
 *
 * Lets the admin wire up an AI provider (OpenAI, Gemini, Anthropic, or the
 * platform's built-in Emergent Universal Key) without ever exposing the raw
 * API key to the browser. The full key is never returned by the backend;
 * responses only include a masked preview (`sk-••••1234`).
 *
 * State machine:
 *   idle → editing (admin typing a key) → saving → idle (masked preview)
 *                                     → testing (live-probe provider)
 */
import { useEffect, useState } from "react";
import { Eye, EyeOff, Save, PlugZap, Trash2, ShieldCheck, AlertTriangle, HelpCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "openai", label: "OpenAI (GPT / DALL·E)" },
  { id: "gemini", label: "Google Gemini" },
  { id: "anthropic", label: "Anthropic Claude" },
  { id: "emergent", label: "Emergent AI / Universal Key" },
];

function StatusPill({ status }) {
  if (status === "connected") {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold" data-testid="ai-status-connected"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> AI Connected</span>;
  }
  if (status === "invalid") {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 text-xs font-semibold" data-testid="ai-status-invalid"><AlertTriangle className="h-3 w-3" /> Invalid API Key</span>;
  }
  if (status === "error") {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 text-rose-800 border border-rose-200 text-xs font-semibold" data-testid="ai-status-error"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> AI Not Connected</span>;
  }
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600 border border-neutral-200 text-xs font-semibold" data-testid="ai-status-unknown"><HelpCircle className="h-3 w-3" /> Not tested yet</span>;
}

export default function AISettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-settings");
      setSettings(data);
      if (data.provider) setProvider(data.provider);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) {
      toast.error("Enter an API key");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/ai-settings", { provider, api_key: apiKey.trim() });
      toast.success("AI API key saved");
      setApiKey("");
      setShowKey(false);
      setReplaceMode(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/ai-settings/test");
      if (data.status === "connected") toast.success("AI provider connected");
      else if (data.status === "invalid") toast.error(`Invalid key (${data.detail})`);
      else toast.error(`Connection failed (${data.detail})`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete the stored AI API key? AI features will stop working until a new key is saved.")) return;
    try {
      await api.delete("/admin/ai-settings");
      toast.success("AI key deleted");
      setReplaceMode(false);
      setApiKey("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const isConfigured = settings?.configured;
  const showEditor = !isConfigured || replaceMode;

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-neutral-900">AI Settings</h2>
          <p className="text-sm text-neutral-500 mt-1">
            Wire up an AI provider for the platform's AI features. The key is encrypted at
            rest on the backend and <strong>never</strong> exposed to the browser.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : (
        <div className="rounded-2xl border border-border bg-white p-5 space-y-5" data-testid="ai-settings-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-semibold text-neutral-800">Connection status</div>
            <StatusPill status={settings?.connection || "unknown"} />
          </div>

          {isConfigured && !replaceMode && (
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3.5" data-testid="ai-current-provider">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">Current provider</div>
              <div className="mt-1 font-semibold text-neutral-900">
                {PROVIDERS.find((p) => p.id === settings.provider)?.label || settings.provider}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500">API key</div>
              <div className="mt-1 font-mono text-sm text-neutral-800" data-testid="ai-masked-key">
                {settings.masked_key || "•".repeat(24)}
              </div>
              {settings.last_tested_at && (
                <div className="mt-2 text-[11px] text-neutral-500">
                  Last tested: {new Date(settings.last_tested_at).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {showEditor && (
            <div className="space-y-3" data-testid="ai-editor">
              <div>
                <Label className="text-xs font-semibold text-neutral-700">AI Provider</Label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="mt-1 block w-full h-10 rounded-xl border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="ai-provider-select"
                >
                  {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-neutral-700">API Key</Label>
                <div className="mt-1 relative">
                  <Input
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="pr-10 font-mono"
                    autoComplete="off"
                    data-testid="ai-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800"
                    title={showKey ? "Hide key" : "Show key"}
                    data-testid="ai-key-toggle"
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="mt-1.5 text-[11px] text-neutral-500 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3 text-emerald-600" />
                  Stored encrypted on the server. Only a masked preview is ever returned to this page.
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center pt-1">
            {showEditor ? (
              <>
                <Button onClick={save} disabled={saving || !apiKey.trim()} data-testid="ai-save-btn" className="rounded-full">
                  <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save API Key"}
                </Button>
                {isConfigured && (
                  <Button variant="outline" onClick={() => { setReplaceMode(false); setApiKey(""); setShowKey(false); }} className="rounded-full" data-testid="ai-cancel-replace">
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button onClick={test} disabled={testing} data-testid="ai-test-btn" className="rounded-full" variant="secondary">
                  <PlugZap className="h-4 w-4 mr-1.5" /> {testing ? "Testing…" : "Test Connection"}
                </Button>
                <Button variant="outline" onClick={() => setReplaceMode(true)} data-testid="ai-replace-btn" className="rounded-full">
                  Replace Key
                </Button>
                <Button variant="ghost" onClick={remove} data-testid="ai-delete-btn" className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-full ml-auto">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
